import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { BookOpen, Loader2, Star } from "lucide-react";

const CATS = [
  { key: "ideas", label: "Ideas" },
  { key: "organization", label: "Organization" },
  { key: "voice", label: "Voice" },
  { key: "word_choice", label: "Word Choice" },
  { key: "conventions", label: "Conventions" },
];

export default function RubricFeedback() {
  const [prompt, setPrompt] = useState("");
  const [writing, setWriting] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onScore = async () => {
    if (!writing.trim() || !prompt.trim()) { toast.error("Enter both prompt and writing"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/coach/rubric-feedback", { prompt, student_writing: writing });
      setResult(data);
      celebrateBadges(data._meta);
    } catch { toast.error("Could not score"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center"><BookOpen size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Rubric Feedback</h1>
        </div>
        <p className="text-slate-600">Get fair, kind, specific feedback across five rubric traits.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Prompt</label>
          <textarea data-testid="rubric-prompt" value={prompt} onChange={(e)=>setPrompt(e.target.value)} rows={2}
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 px-4 py-3 outline-none resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your writing</label>
          <textarea data-testid="rubric-writing" value={writing} onChange={(e)=>setWriting(e.target.value)} rows={8}
            placeholder="Paste your draft to get feedback..."
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 px-4 py-3 outline-none resize-none" />
        </div>
        <button data-testid="rubric-submit" onClick={onScore} disabled={loading}
          className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
          style={{boxShadow:'0 4px 0 0 rgb(4 120 87)'}}>
          {loading ? <><Loader2 className="animate-spin" size={16}/> Scoring...</> : "Get my feedback"}
        </button>
      </div>

      {result && (
        <div data-testid="rubric-result" className="space-y-4 fade-up">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {CATS.map(c => {
              const v = result.scores?.[c.key] ?? 0;
              return (
                <div key={c.key} className="tactile-card p-4 text-center">
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-500">{c.label}</div>
                  <div className="text-3xl font-bold mt-2" style={{fontFamily:'Outfit'}}>{v}<span className="text-base text-slate-400">/4</span></div>
                  <div className="flex justify-center gap-0.5 mt-2">
                    {[1,2,3,4].map(n=>(<Star key={n} size={14} className={n<=v?'fill-amber-400 text-amber-400':'text-slate-300'} />))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
            <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-2">Overall</div>
            <p className="text-slate-900">{result.overall}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="tactile-card p-6 bg-emerald-50 border-emerald-100">
              <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-2">Strengths</div>
              <ul className="list-disc pl-5 space-y-1 text-slate-800">
                {(result.strengths||[]).map((s,i)=>(<li key={i}>{s}</li>))}
              </ul>
            </div>
            <div className="tactile-card p-6 bg-amber-50 border-amber-100">
              <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-2">Growth areas</div>
              <ul className="space-y-3 text-slate-800">
                {(result.growth_areas||[]).map((g,i)=>(
                  <li key={i}>
                    <div className="font-bold text-slate-900">{g.area}</div>
                    <div className="text-sm">{g.why}</div>
                    <div className="text-sm mt-1"><span className="font-bold text-amber-800">Try this:</span> {g.try_this}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="tactile-card p-6 bg-rose-50 border-rose-100">
            <div className="text-xs uppercase font-bold tracking-widest text-rose-800 mb-2">Reflect</div>
            <ul className="list-decimal pl-5 space-y-1 text-slate-800">
              {(result.revision_questions||[]).map((q,i)=>(<li key={i}>{q}</li>))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
